import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { BaseChartComponent } from "./base-chart.component";
import { ChartConfiguration, Plugin } from "chart.js/auto";
import { DataPointDto } from "src/app/services/api";
import { TranslateService } from "@ngx-translate/core";
import {
  CHART_LEVEL1_ZONE_COLOR,
  CHART_LEVEL2_ZONE_COLOR,
  CHART_LEVEL3_ZONE_COLOR,
  CHART_LEVEL4_ZONE_COLOR,
  PRIMARY_LINE_COLOR,
  SECONDARY_LINE_COLOR,
  FONT,
  LABELS_COLOR,
} from "src/app/constants/index";

// Colores para temperaturas mínimas (tonos azules/fríos)
const CHART_MIN_LEVEL1_ZONE_COLOR = CHART_LEVEL2_ZONE_COLOR;
const CHART_MIN_LEVEL2_ZONE_COLOR = CHART_LEVEL3_ZONE_COLOR;
const CHART_MIN_LEVEL3_ZONE_COLOR = CHART_LEVEL4_ZONE_COLOR;

@Component({
  selector: "app-temperature-chart",
  templateUrl: "./chart.component.html",
  styleUrls: ["./chart.component.scss"],
})
export class TemperatureChartComponent extends BaseChartComponent implements OnChanges {
  @Input() public humidityData?: DataPointDto[];

  private readonly TEMP_MIN_THRESHOLDS = [-1, -4, -8];
  private readonly TEMP_MAX_THRESHOLDS = [36, 39, 42];

  private calculatedYMin: number = -10;
  private calculatedYMax: number = 50;

  private temperatureLabel: string;
  private humidityLabel: string;

  constructor(translate: TranslateService, cdr: ChangeDetectorRef) {
    super(translate, cdr);
    this.temperatureLabel = translate.instant("STATION_PAGE.TEMPERATURE") + " (°C)";
    this.humidityLabel = translate.instant("STATION_PAGE.AIR_HUMIDITY") + " (%)";
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Update the data property for app-empty-data component
    if (this.data?.length || this.humidityData?.length) {
      // Force chart re-rendering when data changes
      if ((changes.data || changes.humidityData) && this.chartContainer) {
        setTimeout(() => {
          this.renderChart();
          this.cdr.detectChanges();
        }, 0);
        return;
      }
    }

    // Call parent's ngOnChanges to ensure base component logic runs
    super.ngOnChanges(changes);
  }

  private drawTemperatureAlertLevelsPlugin: Plugin = {
    id: "temperatureAlerts",
    beforeDraw: (): void => {
      if (this.chart) {
        const {
          ctx,
          chartArea: { left, top, right, bottom },
          scales: { y },
        } = this.chart;

        const yMin = this.calculatedYMin;
        const yMax = this.calculatedYMax;

        // Solo proceder si algún umbral está dentro del rango visible
        const shouldDrawMaxAlerts = this.TEMP_MAX_THRESHOLDS.some(threshold => threshold >= yMin && threshold <= yMax);
        const shouldDrawMinAlerts = this.TEMP_MIN_THRESHOLDS.some(threshold => threshold >= yMin && threshold <= yMax);
        const shouldDrawNormalZone = yMin <= this.TEMP_MAX_THRESHOLDS[0] && yMax >= this.TEMP_MIN_THRESHOLDS[0];

        if (!shouldDrawMaxAlerts && !shouldDrawMinAlerts && !shouldDrawNormalZone) {
          return;
        }

        // Obtener posiciones en píxeles para los umbrales máximos
        const maxLevel1 = y.getPixelForValue(this.TEMP_MAX_THRESHOLDS[0]); // 36°C
        const maxLevel2 = y.getPixelForValue(this.TEMP_MAX_THRESHOLDS[1]); // 39°C
        const maxLevel3 = y.getPixelForValue(this.TEMP_MAX_THRESHOLDS[2]); // 42°C

        // Obtener posiciones en píxeles para los umbrales mínimos
        const minLevel1 = y.getPixelForValue(this.TEMP_MIN_THRESHOLDS[0]); // -1°C
        const minLevel2 = y.getPixelForValue(this.TEMP_MIN_THRESHOLDS[1]); // -4°C
        const minLevel3 = y.getPixelForValue(this.TEMP_MIN_THRESHOLDS[2]); // -8°C

        ctx.save();

        // Zona normal (entre umbrales mínimos y máximos)
        if (shouldDrawNormalZone) {
          const normalZoneTop = Math.max(maxLevel1, top);
          const normalZoneBottom = Math.min(minLevel1, bottom);
          if (normalZoneBottom > normalZoneTop) {
            ctx.fillStyle = CHART_LEVEL1_ZONE_COLOR;
            ctx.fillRect(left, normalZoneTop, right - left, normalZoneBottom - normalZoneTop);
          }
        }

        // Zonas de temperatura máxima (solo si están en el rango visible)
        if (shouldDrawMaxAlerts) {
          // Zona de alerta nivel 1 calor (36°C - 39°C)
          if (this.TEMP_MAX_THRESHOLDS[0] >= yMin && this.TEMP_MAX_THRESHOLDS[0] <= yMax && maxLevel1 >= top && maxLevel1 <= bottom) {
            const level2Top = Math.max(maxLevel2, top);
            const level1Bottom = Math.min(maxLevel1, bottom);
            if (level1Bottom > level2Top) {
              ctx.fillStyle = CHART_LEVEL2_ZONE_COLOR;
              ctx.fillRect(left, level2Top, right - left, level1Bottom - level2Top);
            }
          }

          // Zona de alerta nivel 2 calor (39°C - 42°C)
          if (this.TEMP_MAX_THRESHOLDS[1] >= yMin && this.TEMP_MAX_THRESHOLDS[1] <= yMax && maxLevel2 >= top && maxLevel2 <= bottom) {
            const level3Top = Math.max(maxLevel3, top);
            const level2Bottom = Math.min(maxLevel2, bottom);
            if (level2Bottom > level3Top) {
              ctx.fillStyle = CHART_LEVEL3_ZONE_COLOR;
              ctx.fillRect(left, level3Top, right - left, level2Bottom - level3Top);
            }
          }

          // Zona de alerta nivel 3 calor (> 42°C)
          if (this.TEMP_MAX_THRESHOLDS[2] >= yMin && this.TEMP_MAX_THRESHOLDS[2] <= yMax && maxLevel3 >= top && maxLevel3 <= bottom) {
            ctx.fillStyle = CHART_LEVEL4_ZONE_COLOR;
            ctx.fillRect(left, top, right - left, Math.min(maxLevel3, bottom) - top);
          }
        }

        // Zonas de temperatura mínima (solo si están en el rango visible)
        if (shouldDrawMinAlerts) {
          // Zona de alerta nivel 1 frío (-1°C a -4°C)
          if (this.TEMP_MIN_THRESHOLDS[0] >= yMin && this.TEMP_MIN_THRESHOLDS[0] <= yMax && minLevel1 >= top && minLevel1 <= bottom) {
            const level1Top = Math.max(minLevel1, top);
            const level2Bottom = Math.min(minLevel2, bottom);
            if (level2Bottom > level1Top) {
              ctx.fillStyle = CHART_MIN_LEVEL1_ZONE_COLOR;
              ctx.fillRect(left, level1Top, right - left, level2Bottom - level1Top);
            }
          }

          // Zona de alerta nivel 2 frío (-4°C a -8°C)
          if (this.TEMP_MIN_THRESHOLDS[1] >= yMin && this.TEMP_MIN_THRESHOLDS[1] <= yMax && minLevel2 >= top && minLevel2 <= bottom) {
            const level2Top = Math.max(minLevel2, top);
            const level3Bottom = Math.min(minLevel3, bottom);
            if (level3Bottom > level2Top) {
              ctx.fillStyle = CHART_MIN_LEVEL2_ZONE_COLOR;
              ctx.fillRect(left, level2Top, right - left, level3Bottom - level2Top);
            }
          }

          // Zona de alerta nivel 3 frío (< -8°C)
          if (this.TEMP_MIN_THRESHOLDS[2] >= yMin && this.TEMP_MIN_THRESHOLDS[2] <= yMax && minLevel3 >= top && minLevel3 <= bottom) {
            const level3Top = Math.max(minLevel3, top);
            ctx.fillStyle = CHART_MIN_LEVEL3_ZONE_COLOR;
            ctx.fillRect(left, level3Top, right - left, bottom - level3Top);
          }
        }

        ctx.restore();
      }
    },
  };

  /**
   * Calcula los límites del eje Y según las nuevas reglas de márgenes
   */
  private calculateYAxisLimits(minTemp: number, maxTemp: number): { min: number; max: number } {
    let ySuggestedMin = Math.floor(minTemp / 5) * 5;
    let ySuggestedMax = Math.ceil(maxTemp / 5) * 5;

    const maxDistance = Math.ceil(maxTemp) - ySuggestedMax;
    const minDistance = ySuggestedMin - Math.floor(minTemp);
    // Si los límites están cerca del máximo (a 1 unidad) sumamos 5 al maximo
    if (maxDistance >= -1) {
      ySuggestedMax += 5;
    }
    if (minDistance >= -1) {
      ySuggestedMin -= 5;
    }
    // Ensure there's a visible range if all data points are the same multiple of 5
    if (ySuggestedMin === ySuggestedMax) {
      ySuggestedMin -= 5;
      ySuggestedMax += 5;
    }

    return {
      min: ySuggestedMin,
      max: ySuggestedMax,
    };
  }

  protected getChartConfig(): ChartConfiguration {
    const transformedData = this.data.map(point => {
      const timestamp = new Date(point.timestamp).getTime();
      if (!(point.value || point.value === 0)) {
        return { x: timestamp, y: null };
      }
      return { x: timestamp, y: point.value };
    });

    const xSuggestedMax = !this.currentTime ? new Date().getTime() : undefined;

    // Calculate dynamic y-axis limits based on transformed data
    const validYValues = transformedData.map(point => point.y).filter(value => value !== null);

    let ySuggestedMin: number;
    let ySuggestedMax: number;

    if (validYValues.length > 0) {
      const minTemp = Math.min(...validYValues);
      const maxTemp = Math.max(...validYValues);

      // Aplicar las nuevas reglas de márgenes
      const limits = this.calculateYAxisLimits(minTemp, maxTemp);
      ySuggestedMin = limits.min;
      ySuggestedMax = limits.max;
    } else {
      // Valores por defecto cuando no hay datos
      ySuggestedMin = -10;
      ySuggestedMax = 50;
    }

    this.calculatedYMin = ySuggestedMin;
    this.calculatedYMax = ySuggestedMax;

    // Get common options
    const commonOptions = this.getCommonChartOptions(xSuggestedMax, ySuggestedMax, "°C", ySuggestedMin);

    // Verify that scales exist (they always should from getCommonChartOptions)
    if (!commonOptions.scales?.y) {
      throw new Error("Chart scales not properly initialized");
    }

    const yScaleGrid = commonOptions.scales.y.grid;
    const yScaleTicks = commonOptions.scales.y.ticks;

    // Si existe humidityData se manejan dos datasets con doble eje Y
    if (this.humidityData) {
      const transformedHumidityData = this.humidityData.map(point => {
        const timestamp = new Date(point.timestamp).getTime();
        if (!(point.value || point.value === 0)) {
          return { x: timestamp, y: null };
        }
        return { x: timestamp, y: point.value };
      });

      return {
        type: "line",
        data: {
          datasets: [
            {
              data: transformedData,
              label: this.temperatureLabel,
              yAxisID: "y",
              borderColor: PRIMARY_LINE_COLOR,
              pointBackgroundColor: PRIMARY_LINE_COLOR,
              backgroundColor: PRIMARY_LINE_COLOR,
              borderWidth: 2,
              pointRadius: 1,
              tension: 0.5,
              parsing: false,
            },
            {
              data: transformedHumidityData,
              label: this.humidityLabel,
              yAxisID: "y1",
              borderColor: SECONDARY_LINE_COLOR,
              pointBackgroundColor: SECONDARY_LINE_COLOR,
              backgroundColor: SECONDARY_LINE_COLOR,
              borderWidth: 1.5,
              pointRadius: 1,
              tension: 0.5,
              parsing: false,
            },
          ],
        },
        options: {
          ...commonOptions,
          plugins: {
            legend: {
              display: true,
              labels: {
                boxHeight: 2,
                boxWidth: 24,
              },
            },
          },
          scales: {
            ...commonOptions.scales,
            y: {
              type: "linear",
              position: "left",
              min: ySuggestedMin,
              max: ySuggestedMax,
              grid: yScaleGrid,
              ticks: {
                ...yScaleTicks,
                stepSize: 5,
              },
            },
            y1: {
              type: "linear",
              position: "right",
              min: 0,
              max: 100,
              grid: { drawOnChartArea: false },
              ticks: {
                font: FONT,
                color: LABELS_COLOR,
                stepSize: 10,
                callback: (value: number | string): string => `${value}%`,
              },
            },
          },
        },
        plugins: [this.legendSpacingPlugin, this.drawTemperatureAlertLevelsPlugin],
      };
    } else {
      return {
        type: "line",
        data: {
          datasets: [
            {
              data: transformedData,
              borderColor: PRIMARY_LINE_COLOR,
              backgroundColor: PRIMARY_LINE_COLOR,
              borderWidth: 2,
              pointRadius: 1,
              tension: 0.5,
              parsing: false,
            },
          ],
        },
        options: {
          ...commonOptions,
          scales: {
            ...commonOptions.scales,
            y: {
              type: "linear",
              min: ySuggestedMin,
              max: ySuggestedMax,
              grid: yScaleGrid,
              ticks: {
                ...yScaleTicks,
                stepSize: 5,
              },
            },
          },
        },
        plugins: [this.drawTemperatureAlertLevelsPlugin],
      };
    }
  }
}
